import { OASElement, readConfigValue } from '@oas-ui/core'
import { pickOnColor as pickOnColorBase } from '../../shared/on-color.js'
import { normalizeSizeStrict, ALL_SIZES } from '../../shared/size.js'
import { lookupIcon } from '@oas-ui/icons/runtime'
import { hintUnresolvedIcon } from '../../shared/icon-hint.js'
import { devWarn } from '../../shared/dev-warn.js'

export type ButtonType = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'text'
export type ButtonSize = 'xs' | 'small' | 'medium' | 'large' | 'xl'
/** variant 形态维度（正交 type 语义色）：solid 实底 / outlined 描边 / dashed 虚线描边 / filled 浅底 / text 文字 / link 链接 */
export type ButtonVariant = 'solid' | 'outlined' | 'dashed' | 'filled' | 'text' | 'link'
/** 原生表单行为（html-type）：button 默认无表单行为 / submit 提交 / reset 重置 */
export type ButtonHtmlType = 'button' | 'submit' | 'reset'

const VALID_BUTTON_VARIANTS: readonly ButtonVariant[] = ['solid', 'outlined', 'dashed', 'filled', 'text', 'link']
const VALID_HTML_TYPES: readonly ButtonHtmlType[] = ['button', 'submit', 'reset']
/** active-tint 预设色板名（ui-spec §4.1 color 协议词表，映射 --oas-preset-* token；实底选中取 -text 档） */
const PRESET_TINTS: readonly string[] = [
  'magenta',
  'red',
  'volcano',
  'orange',
  'gold',
  'lime',
  'green',
  'cyan',
  'blue',
  'geekblue',
  'purple',
]

/** 非法 size 告警：回落 medium 并在 dev 下 console.warn 一次（同值去重）；sm/md/lg 别名由 shared/size 静默映射，不告警 */
const warnedSizes = new Set<string>()
function warnInvalidSize(raw: string): void {
  if (warnedSizes.has(raw)) return
  warnedSizes.add(raw)
  devWarn(`[oas-button] 非法 size "${raw}"，已回落 medium；合法值：xs/small/medium/large/xl`)
}

/**
 * 实底选中态「on」色（薄封装，共享实现见 `shared/on-color`；不可解析回落 CSS 兜底 token）。
 */
function pickOnColor(color: string): string {
  return pickOnColorBase(color, { dark: '#18181b', light: '#ffffff', fallback: '' })
}

const STYLE = `
:host {
  display: inline-block;
  font-family: inherit;
}
:host([hidden]) {
  display: none;
}
:host([block]) {
  display: block;
  width: 100%;
}
:host([block]) button {
  width: 100%;
}
button,
a[part='button'] {
  appearance: none;
  filter: var(--oas-glass-refraction, none);
  border: 1px solid var(--oas-color-border);
  background: var(--oas-color-bg);
  color: var(--oas-color-text-primary);
  /* color 自定义色的兜底入口：无 type 时 outlined/filled/text 等形态也能吃到 --oas-button-color */
  --btn-color: var(--oas-button-color, var(--oas-color-text-primary));
  font-size: var(--oas-font-size-md);
  /* 高度走变量（档值兜底）：swipe-cell actions 等需要「按钮填满行高」的场景经
     --oas-button-height: 100% 开口注入（::slotted 可设自定义属性穿透）；
     默认回落档值——无开口时行为与固定档值完全一致 */
  height: var(--oas-button-height, var(--oas-control-height-md));
  padding: 0 var(--oas-space-4);
  /* 默认单行不换行（通行做法）；长文换行需显式 wrap 属性（见末尾 .wrap 规则） */
  white-space: nowrap;
  text-align: center;
  /* button-group 通过自定义属性合并相邻圆角/拉满宽度（::slotted 后不支持链 ::part，故走变量穿透） */
  border-radius: var(--oas-button-group-radius, var(--oas-radius-md));
  width: var(--oas-button-group-width, auto);
  cursor: pointer;
  transition: background var(--oas-transition-fast) var(--oas-ease-out),
    border-color var(--oas-transition-fast) var(--oas-ease-out);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--oas-space-1);
  text-decoration: none;
  box-sizing: border-box;
}


button:hover,
a[part='button']:hover {
  background: var(--oas-color-bg-hover);
}
/* 图标按钮：图标与文字间距走 --oas-space-2 */
button.has-icon {
  gap: var(--oas-space-2);
}
/* 纯图标按钮：等宽正方形（width = height），去除水平内边距；优先级高于 size 档的 padding（large/xl 的 :not(.icon-only) 不覆盖） */
button.icon-only {
  aspect-ratio: 1;
  padding: 0 !important;
}
/* 块级按钮占满父容器 */
button.block {
  width: 100%;
}
/* 胶囊圆角（button-group 合并圆角经自定义属性穿透，round 显式覆盖） */
button.round,
a[part='button'].round {
  border-radius: var(--oas-radius-full, 999px);
}
/* 圆形按钮（纯图标常用）：正方形 + 正圆角 */
button.circle,
a[part='button'].circle {
  aspect-ratio: 1;
  padding: 0;
  border-radius: 50%;
}
/* 图标在右：icon 后置到文字之后（icon-end 尾部图标天然在最后，用 :not 排除） */
button.icon-end .icon:not([part='icon-end']),
a[part='button'].icon-end .icon:not([part='icon-end']) {
  order: 2;
}
/* 朴素按钮：弱化填充（浅底 + 主色文字），text 已是朴素 */
button.plain.primary {
  background: color-mix(in srgb, var(--oas-color-primary) 12%, transparent);
  border-color: transparent;
  color: var(--oas-color-primary-text);
}
button.plain.primary:hover {
  background: color-mix(in srgb, var(--oas-color-primary) 20%, transparent);
}
button.plain.primary:active {
  background: color-mix(in srgb, var(--oas-color-primary) 26%, transparent);
}
button.plain.success {
  background: color-mix(in srgb, var(--oas-color-success) 12%, transparent);
  border-color: transparent;
  color: var(--oas-color-success-text);
}
button.plain.success:hover {
  background: color-mix(in srgb, var(--oas-color-success) 20%, transparent);
}
button.plain.warning {
  background: color-mix(in srgb, var(--oas-color-warning) 12%, transparent);
  border-color: transparent;
  color: var(--oas-color-warning-text);
}
button.plain.warning:hover {
  background: color-mix(in srgb, var(--oas-color-warning) 20%, transparent);
}
button.plain.danger {
  background: color-mix(in srgb, var(--oas-color-danger) 12%, transparent);
  border-color: transparent;
  color: var(--oas-color-danger-text);
}
button.plain.danger:hover {
  background: color-mix(in srgb, var(--oas-color-danger) 20%, transparent);
}

/* 链接按钮禁用态（a 无 disabled 属性，用 aria-disabled + 与 button 一致配色） */
a[part='button'][aria-disabled='true'] {
  cursor: not-allowed;
  opacity: 0.6;
  background: var(--oas-color-bg-disabled);
  color: var(--oas-color-text-disabled);
  /* 禁用态关闭折射（与 disabled-focusable 一致） */
  filter: none;
}
a[part='button'][aria-disabled='true']:hover {
  background: var(--oas-color-bg-disabled);
  color: var(--oas-color-text-disabled);
}
.icon {
  display: inline-flex;
}
.icon[hidden] {
  display: none;
}
button:focus-visible {
  outline: none;
  box-shadow: var(--oas-focus-ring);
}
/* 选中态（button-group 单/多选经 host aria-pressed 标记；active 属性复用同一挂点，见 update()）；
   置于类型规则前，有色按钮由下方规则覆盖。
   active-tint 选中着色（组件按属性注入 --oas-button-active-tint*，未注入时全部回落 primary 系 token，
   与既有选中视觉零差异）：文字安全档/实底 on 色由 JS 侧按色值来源派生（预设走 -text 档 token、
   字面色走主题感知混合/亮度判定）。
   有意设计：禁用 × 选中时颜色维度的选中态不被禁用规则反超（特异性更高）——禁用组里
   当前选中项保持主色淡底/描边（经 opacity 0.6 柔化），用户能读到「当前状态是什么」；
   全灰会丢失状态信息（通行做法：禁用只灭交互与装饰变形，不抹状态）。折射/亮度等
   装饰变形则由下方禁用守卫钉死 */
:host([aria-pressed='true']) button,
:host([aria-pressed='true']) a[part='button'] {
  color: var(--oas-button-active-tint-text, var(--oas-color-primary-text));
  border-color: var(--oas-button-active-tint, var(--oas-color-primary));
  background: color-mix(in srgb, var(--oas-button-active-tint, var(--oas-color-primary)) 12%, transparent);
}
button[part~='button'][disabled],
button[disabled] {
  cursor: not-allowed;
  opacity: 0.6;
  background: var(--oas-color-bg-disabled);
  color: var(--oas-color-text-disabled);
  /* 禁用态关闭折射（与 disabled-focusable 一致：禁用外观恒定不变形） */
  filter: none;
}
button.primary {
  background: var(--oas-color-primary);
  border-color: var(--oas-color-primary);
  color: var(--oas-color-text-on-primary);
}
a[part='button'].primary {
  background: var(--oas-color-primary);
  border-color: var(--oas-color-primary);
  color: var(--oas-color-text-on-primary);
}
button.primary:hover {
  background: var(--oas-color-primary-hover);
  border-color: var(--oas-color-primary-hover);
}
button.primary:active {
  background: var(--oas-color-primary-active);
  border-color: var(--oas-color-primary-active);
}
button.success {
  background: color-mix(in srgb, var(--oas-color-success) 80%, var(--oas-color-text-primary));
  border-color: color-mix(in srgb, var(--oas-color-success) 80%, var(--oas-color-text-primary));
  color: var(--oas-color-text-on-success);
}
a[part='button'].success {
  background: color-mix(in srgb, var(--oas-color-success) 80%, var(--oas-color-text-primary));
  border-color: color-mix(in srgb, var(--oas-color-success) 80%, var(--oas-color-text-primary));
  color: var(--oas-color-text-on-success);
}
button.success:hover {
  /* hover 变暗、选中更深（0.94 → 0.85）：与 primary 的 color-mix 加深体系同向递进 */
  filter: brightness(0.94) var(--oas-glass-refraction, );
}
button.warning {
  background: color-mix(in srgb, var(--oas-color-warning) 80%, var(--oas-color-text-primary));
  border-color: color-mix(in srgb, var(--oas-color-warning) 80%, var(--oas-color-text-primary));
  color: var(--oas-color-text-on-warning);
}
a[part='button'].warning {
  background: color-mix(in srgb, var(--oas-color-warning) 80%, var(--oas-color-text-primary));
  border-color: color-mix(in srgb, var(--oas-color-warning) 80%, var(--oas-color-text-primary));
  color: var(--oas-color-text-on-warning);
}
button.warning:hover {
  filter: brightness(0.94) var(--oas-glass-refraction, );
}
button.danger {
  background: color-mix(in srgb, var(--oas-color-danger) 80%, var(--oas-color-text-primary));
  border-color: color-mix(in srgb, var(--oas-color-danger) 80%, var(--oas-color-text-primary));
  color: var(--oas-color-text-on-danger);
}
a[part='button'].danger {
  background: color-mix(in srgb, var(--oas-color-danger) 80%, var(--oas-color-text-primary));
  border-color: color-mix(in srgb, var(--oas-color-danger) 80%, var(--oas-color-text-primary));
  color: var(--oas-color-text-on-danger);
}
button.danger:hover {
  filter: brightness(0.94) var(--oas-glass-refraction, );
}
/* 有色 / text 按钮的选中态覆盖（选中比 hover 更深一档：0.85 vs 0.94）。
   anchor 变体必须与 button 一样带 :host([aria-pressed]) 前缀——曾现 bug：a 镜像规则丢了前缀，
   带 href 的 primary/text 按钮静止时永久显示选中色 */
:host([aria-pressed='true']) button.primary,
:host([aria-pressed='true']) a[part='button'].primary {
  background: var(--oas-button-active-tint, var(--oas-color-primary-active));
  border-color: var(--oas-button-active-tint, var(--oas-color-primary-active));
  /* 选中态基础规则（:host([aria-pressed]) button）把文字设为主色/tint，primary 深底上需 on 色 */
  color: var(--oas-button-active-tint-on, var(--oas-color-text-on-primary));
}
:host([aria-pressed='true']) button.success,
:host([aria-pressed='true']) button.warning,
:host([aria-pressed='true']) button.danger,
:host([aria-pressed='true']) a[part='button'].success,
:host([aria-pressed='true']) a[part='button'].warning,
:host([aria-pressed='true']) a[part='button'].danger {
  filter: brightness(0.85) var(--oas-glass-refraction, );
}
:host([aria-pressed='true']) button.text,
:host([aria-pressed='true']) a[part='button'].text {
  color: var(--oas-button-active-tint-text, var(--oas-color-primary-text));
  background: color-mix(in srgb, var(--oas-button-active-tint, var(--oas-color-primary)) 12%, transparent);
}
button.small {
  height: var(--oas-button-height, var(--oas-control-height-sm));
  font-size: var(--oas-font-size-sm);
  padding: 0 var(--oas-space-2);
}
a[part='button'].small {
  height: var(--oas-button-height, var(--oas-control-height-sm));
  font-size: var(--oas-font-size-sm);
  padding: 0 var(--oas-space-2);
}
button.xs {
  height: var(--oas-button-height, var(--oas-control-height-xs));
  font-size: var(--oas-font-size-xs);
}
a[part='button'].xs {
  height: var(--oas-control-height-xs);
  font-size: var(--oas-font-size-xs);
}
button.xs:not(.icon-only) {
  padding: 0 6px;
  /* xs 档防过窄：最小宽放宽至 44px（其余档无最小宽约束） */
  min-width: 44px;
}
button.large {
  height: var(--oas-button-height, var(--oas-control-height-lg));
  font-size: var(--oas-font-size-lg);
  padding: 0 var(--oas-space-5);
}
a[part='button'].large {
  height: var(--oas-button-height, var(--oas-control-height-lg));
  font-size: var(--oas-font-size-lg);
  padding: 0 var(--oas-space-5);
}
button.xl {
  height: var(--oas-button-height, var(--oas-control-height-xl));
  font-size: var(--oas-font-size-xl);
}
a[part='button'].xl {
  height: var(--oas-button-height, var(--oas-control-height-xl));
  font-size: var(--oas-font-size-xl);
}
button.xl:not(.icon-only) {
  padding: 0 var(--oas-space-6);
}
/* wrap 长文换行（显式属性开启）：white-space 恢复正常、盒随内容长高，单行时与默认等高（min-height 兜底） */
button.wrap,
a[part='button'].wrap {
  white-space: normal;
  height: auto;
  min-height: var(--oas-control-height-md);
  padding-block: 2px;
}
button.small.wrap,
a[part='button'].small.wrap {
  min-height: var(--oas-control-height-sm);
}
button.xs.wrap,
a[part='button'].xs.wrap {
  min-height: var(--oas-control-height-xs);
}
button.large.wrap,
a[part='button'].large.wrap {
  min-height: var(--oas-control-height-lg);
}
button.xl.wrap,
a[part='button'].xl.wrap {
  min-height: var(--oas-control-height-xl);
}
button.text {
  border-color: transparent;
  background: transparent;
}
a[part='button'].text {
  border-color: transparent;
  background: transparent;
}
/* 幽灵按钮：透明底 + 描边，按 type 着色，hover 加深 */
button.ghost {
  background: transparent;
  border-color: var(--oas-color-border);
  color: var(--oas-color-text-primary);
}
a[part='button'].ghost {
  background: transparent;
  border-color: var(--oas-color-border);
  color: var(--oas-color-text-primary);
}
button.ghost:hover {
  background: var(--oas-color-bg-hover);
  border-color: var(--oas-color-border-strong);
}
button.ghost.primary {
  background: transparent;
  border-color: var(--oas-color-primary);
  color: var(--oas-color-primary);
}
a[part='button'].ghost.primary {
  background: transparent;
  border-color: var(--oas-color-primary);
  color: var(--oas-color-primary);
}
button.ghost.primary:hover {
  background: color-mix(in srgb, var(--oas-color-primary) 10%, transparent);
  border-color: var(--oas-color-primary-hover);
  color: var(--oas-color-primary-hover);
}
button.ghost.primary:active {
  background: color-mix(in srgb, var(--oas-color-primary) 16%, transparent);
  border-color: var(--oas-color-primary-active);
  color: var(--oas-color-primary-active);
}
button.ghost.success {
  background: transparent;
  /* 文字/描边用文字安全档（light 掺主文本色≈掺黑压深、dark 提亮），深浅底双达标；
     主题感知掺向替代固定掺黑（掺黑在暗色下把文字拉回不可读） */
  border-color: color-mix(in srgb, var(--oas-color-success) 80%, var(--oas-color-text-primary));
  color: color-mix(in srgb, var(--oas-color-success) 80%, var(--oas-color-text-primary));
}
a[part='button'].ghost.success {
  background: transparent;
  border-color: color-mix(in srgb, var(--oas-color-success) 80%, var(--oas-color-text-primary));
  color: color-mix(in srgb, var(--oas-color-success) 80%, var(--oas-color-text-primary));
}
button.ghost.success:hover {
  background: color-mix(in srgb, var(--oas-color-success) 10%, transparent);
  border-color: color-mix(in srgb, var(--oas-color-success) 70%, var(--oas-color-text-primary));
  color: color-mix(in srgb, var(--oas-color-success) 70%, var(--oas-color-text-primary));
}
button.ghost.warning {
  background: transparent;
  border-color: color-mix(in srgb, var(--oas-color-warning) 80%, var(--oas-color-text-primary));
  color: color-mix(in srgb, var(--oas-color-warning) 80%, var(--oas-color-text-primary));
}
a[part='button'].ghost.warning {
  background: transparent;
  border-color: color-mix(in srgb, var(--oas-color-warning) 80%, var(--oas-color-text-primary));
  color: color-mix(in srgb, var(--oas-color-warning) 80%, var(--oas-color-text-primary));
}
button.ghost.warning:hover {
  background: color-mix(in srgb, var(--oas-color-warning) 10%, transparent);
  border-color: color-mix(in srgb, var(--oas-color-warning) 70%, var(--oas-color-text-primary));
  color: color-mix(in srgb, var(--oas-color-warning) 70%, var(--oas-color-text-primary));
}
button.ghost.danger {
  background: transparent;
  border-color: var(--oas-color-danger);
  color: var(--oas-color-danger);
}
a[part='button'].ghost.danger {
  background: transparent;
  border-color: var(--oas-color-danger);
  color: var(--oas-color-danger);
}
button.ghost.danger:hover {
  background: color-mix(in srgb, var(--oas-color-danger) 10%, transparent);
  border-color: color-mix(in srgb, var(--oas-color-danger) 70%, var(--oas-color-text-primary));
  color: color-mix(in srgb, var(--oas-color-danger) 70%, var(--oas-color-text-primary));
}
/* 幽灵禁用：回落到禁用配色，防止 ghost/ghost:hover 覆盖 disabled 样式 */
button.ghost[disabled],
button.ghost[disabled]:hover,
button.ghost[disabled]:active {
  background: var(--oas-color-bg-disabled);
  border-color: var(--oas-color-border);
  color: var(--oas-color-text-disabled);
}
a[part='button'].ghost[disabled]:active {
  background: var(--oas-color-bg-disabled);
  border-color: var(--oas-color-border);
  color: var(--oas-color-text-disabled);
}
/* ===== variant 形态维度（正交 type 语义色）===== */
/* 语义色变量：type 决定；color 属性经 --oas-button-color 覆盖 */
button.primary,
a[part='button'].primary {
  --btn-color: var(--oas-button-color, var(--oas-color-primary));
}
button.success,
a[part='button'].success {
  --btn-color: var(--oas-button-color, color-mix(in srgb, var(--oas-color-success) 80%, var(--oas-color-text-primary)));
}
button.warning,
a[part='button'].warning {
  --btn-color: var(--oas-button-color, color-mix(in srgb, var(--oas-color-warning) 80%, var(--oas-color-text-primary)));
}
button.danger,
a[part='button'].danger {
  --btn-color: var(--oas-button-color, var(--oas-color-danger));
}
/* outlined：透明底 + 语义色描边与文字；文字走「文字安全档」deep（对齐 tag outlined 惯例：
   描边本色 + 文字达标档）——自定义色（hex）在深浅主题下的可读性由 --oas-deep-mix/--oas-deep-sink
   主题参数保证，语义色兜底（--btn-color 已主题感知掺向）同样适用 */
button.outlined,
a[part='button'].outlined {
  background: transparent;
  border-color: var(--btn-color, var(--oas-color-border));
  color: var(--oas-button-color-deep, var(--btn-color, var(--oas-color-text-primary)));
}
button.outlined:hover,
a[part='button'].outlined:hover {
  background: color-mix(in srgb, var(--btn-color, var(--oas-color-text-primary)) 8%, transparent);
}
/* dashed：虚线描边（outlined + 虚线样式），文字安全档同 outlined */
button.dashed,
a[part='button'].dashed {
  background: transparent;
  border: 1px dashed var(--btn-color, var(--oas-color-border));
  color: var(--oas-button-color-deep, var(--btn-color, var(--oas-color-text-primary)));
}
button.dashed:hover,
a[part='button'].dashed:hover {
  background: color-mix(in srgb, var(--btn-color, var(--oas-color-text-primary)) 8%, transparent);
}
/* filled：浅底 soft（语义色 12% 底 + 文字安全档） */
button.filled,
a[part='button'].filled {
  background: color-mix(in srgb, var(--btn-color, var(--oas-color-text-primary)) 12%, transparent);
  border-color: transparent;
  color: var(--oas-button-color-deep, var(--btn-color, var(--oas-color-text-primary)));
}
button.filled.primary,
a[part='button'].filled.primary,
button.filled.success,
a[part='button'].filled.success,
button.filled.warning,
a[part='button'].filled.warning,
button.filled.danger,
a[part='button'].filled.danger {
  /* 文字安全档：主题感知混合（light 72% 掺黑压深 / dark 38% 掺主文本色提亮）；
     原「80% 掺黑」固定掺向在暗色下把浅色 token 压回不可读 */
  color: color-mix(in srgb, var(--btn-color) var(--oas-deep-mix, 72%), var(--oas-deep-sink, black));
}
button.filled:hover,
a[part='button'].filled:hover {
  background: color-mix(in srgb, var(--btn-color, var(--oas-color-text-primary)) 18%, transparent);
}
/* text：纯文字（无框无背景） */
button.text,
a[part='button'].text {
  background: transparent;
  border-color: transparent;
  color: var(--btn-color, var(--oas-color-text-primary));
}
button.text:hover,
a[part='button'].text:hover {
  background: color-mix(in srgb, var(--btn-color, var(--oas-color-text-primary)) 8%, transparent);
}
/* link：链接样式（主色文字 + hover 下划线），常配合 href */
button.link,
a[part='button'].link {
  background: transparent;
  border-color: transparent;
  color: var(--oas-button-color, var(--oas-color-primary));
}
button.link:hover,
a[part='button'].link:hover {
  color: var(--oas-button-color, var(--oas-color-primary-hover));
  text-decoration: underline;
  background: transparent;
}
/* solid 背景走 --oas-button-bg 变量（支持渐变覆盖，默认语义色）；仅 solid 生效，避免覆盖 filled/outlined 等形态。
   :not() 链必须包在 :where() 里归零权重——裸写会把规则堆到 (0,6,1)，压死 hover/active/aria-pressed
   选中态的 background（三态视觉反馈全失效，曾现 bug）；:where() 版 (0,1,1) 与上方 button.primary 同权重、
   靠后取胜，恰好只承担默认底色覆盖职责。 */
button.primary:where(:not(.filled):not(.outlined):not(.dashed):not(.text):not(.link)),
a[part='button'].primary:where(:not(.filled):not(.outlined):not(.dashed):not(.text):not(.link)) {
  background: var(--oas-button-bg, var(--oas-color-primary));
}
/* color 自定义色 + solid（默认形态）：无 type 也实心着色（覆盖 type 语义色）。
   :where() 归零 :not() 链权重，与上方类型规则同级靠后取胜；hover/active 等状态规则仍优先 */
button.has-color:where(:not(.filled):not(.outlined):not(.dashed):not(.text):not(.link)),
a[part='button'].has-color:where(:not(.filled):not(.outlined):not(.dashed):not(.text):not(.link)) {
  background: var(--oas-button-bg, var(--oas-button-color));
  border-color: var(--oas-button-bg, var(--oas-button-color));
  color: var(--oas-button-on-color, var(--oas-color-text-on-primary));
}
/* 自定义色实心 hover（主题感知掺向：light 压深 / dark 提亮） */
button.has-color:where(:not(.filled):not(.outlined):not(.dashed):not(.text):not(.link)):hover,
a[part='button'].has-color:where(:not(.filled):not(.outlined):not(.dashed):not(.text):not(.link)):hover {
  background: color-mix(in srgb, var(--oas-button-bg, var(--oas-button-color)) 85%, var(--oas-color-text-primary));
  border-color: color-mix(in srgb, var(--oas-button-bg, var(--oas-button-color)) 85%, var(--oas-color-text-primary));
}
/* press 反馈（wave 默认开）：按下轻微下沉 + 加深，克制不抢眼 */
button.wave,
a[part='button'].wave {
  transition: transform var(--oas-transition-fast) var(--oas-ease-out),
    background var(--oas-transition-fast) var(--oas-ease-out),
    border-color var(--oas-transition-fast) var(--oas-ease-out),
    filter var(--oas-transition-fast) var(--oas-ease-out);
}
button.wave:active,
a[part='button'].wave:active {
  transform: scale(0.97);
  filter: brightness(0.94) var(--oas-glass-refraction, );
}
/* disabled-focusable：视觉禁用（降饱和 + 禁用配色）但保持可聚焦/可 hover（供 tooltip 解释禁用原因）。
   不设原生 disabled；点击由 JS 拦截。置于形态规则之后覆盖 type 的 hover/active 重着色，
   同时归零 wave 的按下反馈（transform/filter），保证禁用外观恒定 */
button.disabled-focusable,
a[part='button'].disabled-focusable,
button.disabled-focusable:hover,
a[part='button'].disabled-focusable:hover,
button.disabled-focusable:active,
a[part='button'].disabled-focusable:active {
  cursor: not-allowed;
  opacity: 0.6;
  background: var(--oas-color-bg-disabled);
  border-color: var(--oas-color-border);
  color: var(--oas-color-text-disabled);
  transform: none;
  filter: none;
}
/* 禁用态守卫（置于全部状态组合滤镜之后）：禁用原生按钮仍匹配 :hover，aria-pressed 选中态
   规则特异性更高——两者都会把 brightness+折射的组合滤镜反超回禁用按钮（review 实抓）。
   三种禁用形态 × hover/active/选中 全部钉死 filter: none（禁用外观恒定不变形不闪断）。
   只钉 filter 不钉 transform：原生 disabled 不触发 :active，wave 的 scale 不会生效
   （disabled-focusable 不设原生 disabled，其 transform 已由上方块归零）。
   维护注意：后续新增组合滤镜状态规则必须写在本块之前，写在本块之后会被守卫反超 */
button[disabled]:hover,
button[disabled]:active,
:host([aria-pressed='true']) button[disabled],
a[part='button'][aria-disabled='true']:hover,
a[part='button'][aria-disabled='true']:active,
:host([aria-pressed='true']) a[part='button'][aria-disabled='true'],
:host([aria-pressed='true']) button.disabled-focusable,
:host([aria-pressed='true']) a[part='button'].disabled-focusable {
  filter: none;
}
/* loading 态宽度稳定：spinner 绝对定位居中不撑宽；原文字/图标仅 visibility 隐藏（保留占位宽度），
   loading 前后按钮宽度不变。带 loading-text 时原内容彻底移除，spinner 与 loading-text 流内布局 */
button.loading,
a[part='button'].loading {
  position: relative;
}
button.loading .spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
button.loading slot:not([name='loading-icon']),
button.loading .icon {
  visibility: hidden;
}
button.loading-with-text slot:not([name='loading-icon']),
button.loading-with-text .icon {
  display: none;
}
button.loading-with-text .spinner {
  position: static;
  transform: none;
}
.spinner {
  width: 1em;
  height: 1em;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: oas-spin 0.8s linear infinite;
}
.spinner[hidden] {
  display: none;
}
/* loading-icon 插槽自定义加载图标：slot 有内容时停用内置环（border/动画/固定尺寸），
   插槽内容原样显示（如 oas-icon spin / 自定义 SVG），定位沿用 spinner 的绝对居中；
   hidden 显式覆盖（[hidden] 与 .custom-icon 同特异性，靠后规则保证隐藏恒生效） */
.spinner.custom-icon {
  width: auto;
  height: auto;
  border: none;
  border-radius: 0;
  animation: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.spinner.custom-icon[hidden] {
  display: none;
}
@keyframes oas-spin {
  to {
    transform: rotate(360deg);
  }
}
/* ===== 触控目标抬升（仅 coarse pointer 命中）：按钮最小高度抬到 --oas-touch-target-min。
   min-height 大于各档固定 height 时按 min-height 取值（xs 20px / md 32px 等被抬到 44px，
   已 ≥44px 的档如 xl 不变）；icon-only / circle 经 aspect-ratio 同步放宽到 44×44。
   只抬 min-height，padding / 字号 / 圆角一律不动。!important 压过 .size.wrap 的
   min-height（如 xs.wrap 的 20px），保证 coarse 下 wrap 按钮同样 ≥44px。
   规则全部收在媒体查询内，fine pointer（PC）零影响。 ===== */
@media (pointer: coarse) {
  button,
  a[part='button'] {
    min-height: var(--oas-touch-target-min, 44px) !important;
  }
}
/* ===== compound 双行变体（slot="description" 副文本行）=====
   不动既有子结构（flat flex 行）：description 部件 flex-basis:100% 强制换行成第二行。
   无内容时 hidden（零回归：无 data-compound 的按钮结构与旧版完全一致）。
   高度自适应与 wrap 同机制（height:auto + min-height 保档）——按钮高度不再锁死，
   副文本行不溢出按钮框（浏览器截图实抓：固定 height 会裁掉第二行）。 */
[part='description'] {
  font-size: var(--oas-font-size-sm);
  color: var(--oas-color-text-secondary);
  line-height: 1.4;
  text-align: center;
  width: 100%;
}
:host([data-compound]) button,
:host([data-compound]) a[part='button'] {
  flex-wrap: wrap;
  row-gap: var(--oas-space-1);
  height: auto;
  min-height: var(--oas-control-height-md);
  padding-block: var(--oas-space-1);
}
:host([data-compound]) button.small,
:host([data-compound]) a[part='button'].small {
  min-height: var(--oas-control-height-sm);
}
:host([data-compound]) button.xs,
:host([data-compound]) a[part='button'].xs {
  min-height: var(--oas-control-height-xs);
}
:host([data-compound]) button.large,
:host([data-compound]) a[part='button'].large {
  min-height: var(--oas-control-height-lg);
}
:host([data-compound]) button.xl,
:host([data-compound]) a[part='button'].xl {
  min-height: var(--oas-control-height-xl);
}
:host([data-compound]) [part='description'] {
  display: block;
}
/* 实底形态副文本：次要色在实底上对比度不达标（a11y 门禁实抓 #71717a on #0b6cff）——
   改用各形态 on 色的淡化档（92% 混合，保持「副文本弱一级」的视觉层级且对比达标） */
button.primary [part='description'],
a[part='button'].primary [part='description'] {
  color: color-mix(in srgb, var(--oas-color-text-on-primary) 92%, transparent);
}
button.success [part='description'],
a[part='button'].success [part='description'] {
  color: color-mix(in srgb, var(--oas-color-text-on-success) 92%, transparent);
}
button.warning [part='description'],
a[part='button'].warning [part='description'] {
  color: color-mix(in srgb, var(--oas-color-text-on-warning) 92%, transparent);
}
button.danger [part='description'],
a[part='button'].danger [part='description'] {
  color: color-mix(in srgb, var(--oas-color-text-on-danger) 92%, transparent);
}
button.has-color:where(:not(.filled):not(.outlined):not(.dashed):not(.text):not(.link)) [part='description'],
a[part='button'].has-color:where(:not(.filled):not(.outlined):not(.dashed):not(.text):not(.link)) [part='description'] {
  color: color-mix(in srgb, var(--oas-button-on-color, var(--oas-color-text-on-primary)) 92%, transparent);
}
:host([data-compound]) [part='description'][hidden] {
  display: none;
}
`

export class OASButton extends OASElement {
  static override get observedAttributes(): string[] {
    return [
      'type',
      'size',
      'disabled',
      'disabled-focusable',
      'loading',
      'loading-text',
      'icon',
      'icon-end',
      'block',
      'round',
      'ghost',
      'circle',
      'icon-position',
      'href',
      'target',
      'download',
      'rel',
      'plain',
      'variant',
      'color',
      'active',
      'active-tint',
      'wave',
      'auto-insert-space',
      'wrap',
      'disabled-skip',
      // 原生表单提交属性组（shadow 内 button 不参与原生表单提交，经 light DOM 代理桥接）
      'html-type',
      'form',
      'formaction',
      'formmethod',
      'formnovalidate',
      'formtarget',
    ]
  }

  private btn: HTMLElement | null = null

  /** loading="auto"：宿主 oas-click 处理返回 Promise 期间是否处于自动加载态 */
  private autoLoading = false
  /** loading="auto"：本次点击收集到的宿主异步处理 Promise（resolve/reject 后退出 loading） */
  private autoPromises: PromiseLike<unknown>[] = []
  /** 被包装的 oas-click 宿主监听器（原函数 → 包装函数），removeEventListener 时反查解绑 */
  private wrappedListeners = new WeakMap<EventListener, EventListener>()
  /** aria-pressed 所有权标记：上次 update 由 active 属性写入时为真——撤除时只回收自己写的，
   *  不动 button-group 对子按钮 aria-pressed 的直接管理（group 读写均不走本组件 update） */
  private ownedAriaPressed = false

  constructor() {
    super()
    // 尽早安装 oas-click 监听包装：宿主监听一般在 connect/render 之后注册，需先于其生效
    this.patchEventListener()
  }

  /** 是否为链接按钮（渲染 <a> 而非 <button>） */
  private isLink(): boolean {
    return this.getAttr('href', '') !== ''
  }

  /** 原生表单行为归一化：合法值原样返回，空/非法值回落 button（不提交不重置，静默不告警） */
  private htmlType(): ButtonHtmlType {
    const raw = this.getAttr('html-type', 'button')
    return (VALID_HTML_TYPES as readonly string[]).includes(raw) ? (raw as ButtonHtmlType) : 'button'
  }

  /**
   * 原生表单提交桥接（html-type=submit/reset 时把点击转发给目标表单）。
   *
   * 核心问题：shadow 内 button 不参与原生表单提交——form 控件收集与 form owner 解析
   * 均走 DOM 树祖先链，shadow 树内的原生按钮与外部 form 无关联，点击不会触发表单行为。
   *
   * 桥接方案：点击期在 light DOM 临时注入一个 visually-hidden 原生 button 作代理 submitter，
   * 镜像 form / formaction / formmethod / formnovalidate / formtarget 后转发 click——
   * 代理是宿主 light DOM 子节点，宿主在 form 内时代理即 form 子树成员（form owner 天然成立），
   * 宿主在 form 外时经 form 属性 id 关联（原生机制）；点击走原生激活行为
   * （submit → form.requestSubmit(proxy)，reset → form.reset()），
   * formaction/formmethod/formnovalidate/formtarget 由原生 submitter 机制承载，无需自行拼装。
   *
   * 代理为「用完即移除」的临时节点而非常驻 light DOM：宿主框架（Vue）对文本子节点补丁走
   * textContent 整写，会静默抹掉常驻代理；临时注入在同一同步任务内完成注入→点击→移除，
   * 不产生渲染帧、不进默认插槽（slot 指向不存在的具名插槽 + hidden 双保险）、无残留。
   * href 链接模式（a 元素）与 html-type=button（默认）不桥接——这些属性静默无效。
   * 无目标表单（无 form 祖先且 form 属性无匹配 id）时原生激活行为自然 no-op。
   */
  private forwardFormAction(): void {
    const type = this.htmlType()
    if (this.isLink() || type === 'button') return
    const proxy = document.createElement('button')
    proxy.setAttribute('data-oas-form-proxy', '')
    // 指向不存在的具名插槽：不会被默认插槽投影进按钮内容（hidden 兜底）
    proxy.setAttribute('slot', 'oas-form-proxy')
    proxy.hidden = true
    proxy.setAttribute('aria-hidden', 'true')
    proxy.type = type
    const formId = this.getAttr('form', '')
    if (formId) proxy.setAttribute('form', formId)
    const formaction = this.getAttr('formaction', '')
    if (formaction) proxy.setAttribute('formaction', formaction)
    const formmethod = this.getAttr('formmethod', '')
    if (formmethod) proxy.setAttribute('formmethod', formmethod)
    if (this.hasAttr('formnovalidate')) proxy.setAttribute('formnovalidate', '')
    const formtarget = this.getAttr('formtarget', '')
    if (formtarget) proxy.setAttribute('formtarget', formtarget)
    this.appendChild(proxy)
    proxy.click()
    proxy.remove()
  }

  /** 纯函数：SSR 快照与客户端渲染共用同一份模板，保证两路径结构严格一致 */
  private template(): string {
    const href = this.getAttr('href', '')
    const target = this.getAttr('target', '')
    const download = this.getAttr('download', '')
    const rel = this.getAttr('rel', '')
    const tag = href ? 'a' : 'button'
    const hrefAttr = [
      href ? ` href="${href}"` : '',
      target ? ` target="${target}"` : '',
      // download 支持空值布尔（浏览器用原链接文件名），用 hasAttr 判断存在即透传
      this.hasAttr('download') ? ` download="${download}"` : '',
      rel ? ` rel="${rel}"` : '',
    ].join('')
    return `
      <style>${STYLE}</style>
      <${tag} part="button"${hrefAttr}>
        <span class="spinner" part="spinner" hidden><slot name="loading-icon"></slot></span>
        <span class="icon" part="icon" aria-hidden="true" hidden></span>
        <slot></slot>
        <span class="loading-text" hidden></span>
        <span class="icon" part="icon-end" aria-hidden="true" hidden></span>
        <span class="description" part="description" hidden><slot name="description"></slot></span>
      </${tag}>
    `
  }

  /** 缓存节点引用 + 绑定事件（render 与水合路径共用） */
  private bind(): void {
    this.btn = this.shadow.querySelector('button[part="button"], a[part="button"]')

    this.btn?.addEventListener('click', (e: MouseEvent) => {
      // 拦截判定在 emit 前快照：loading="auto" 会在 emit 期间同步进入 loading，
      // 转发判定若放到 emit 后会把本次点击自身触发的 auto loading 误判为拦截
      const blocked = this.injectDisabled() || this.hasAttr('disabled-focusable') || this.isLoading()
      if (blocked) {
        e.preventDefault()
        return
      }
      // loading="auto"：宿主 oas-click 处理返回 Promise 期间自动进入 loading（resolve/reject 后退出）
      const isAuto = this.getAttribute('loading') === 'auto'
      if (isAuto) {
        this.autoLoading = true
        this.autoPromises = []
        this.update()
      }
      this.emit('click', { originalEvent: e })
      // 原生表单提交桥接（html-type=submit/reset）：oas-click 派发后转发
      if (!blocked) this.forwardFormAction()
      if (isAuto) {
        if (this.autoPromises.length > 0) {
          // allSettled：任一 reject 也不提前终止，全部 settle 后统一退出
          void Promise.allSettled(this.autoPromises).then(() => this.exitAutoLoading())
        } else {
          // 宿主未返回 Promise：同步退出，不留 loading 残影
          this.exitAutoLoading()
        }
      }
    })

    // 文字经 slot 增删时重算「纯图标 / 有文字」布局（显式默认插槽选择器：
    // loading-icon 具名插槽在模板序上更靠前，裸 'slot' 会绑错节点）
    this.shadow.querySelector('slot:not([name])')?.addEventListener('slotchange', () => this.update())

    // loading-icon 自定义加载图标：插槽内容增删时刷新 custom-icon 形态（slotchange 感知）
    this.shadow
      .querySelector<HTMLSlotElement>('slot[name="loading-icon"]')
      ?.addEventListener('slotchange', () => this.update())

    // compound 双行变体：description 具名插槽内容增删 → 切换 data-compound（slotchange 感知动态增删）
    this.shadow
      .querySelector<HTMLSlotElement>('slot[name="description"]')
      ?.addEventListener('slotchange', () => this.syncCompound())
    this.syncCompound()

    // autofocus：转发到内部按钮（原生 autofocus 不穿透 shadow，挂载后手动聚焦一次）
    if (this.hasAttr('autofocus')) {
      queueMicrotask(() => this.btn?.focus())
    }
  }

  /** compound 双行变体同步：slot="description" 有实际内容 → data-compound 双行布局；无内容（含空白占位元素）→ description 部件隐藏（零回归） */
  private syncCompound(): void {
    const desc = this.btn?.querySelector<HTMLElement>('[part="description"]')
    if (!desc) return
    const descEl = this.querySelector<HTMLElement>('[slot="description"]')
    const has = descEl !== null && ((descEl.textContent ?? '').trim() !== '' || descEl.children.length > 0)
    this.toggleAttribute('data-compound', has)
    desc.hidden = !has
  }

  /**
   * 包装宿主 addEventListener/removeEventListener（仅 oas-click 生效）：捕获宿主监听器
   * 返回的 Promise，供 loading="auto" 自动 loading 使用。其余事件原样透传；
   * 走原型方法避免递归（自身已被重写）。
   */
  private patchEventListener(): void {
    const self = this
    const addOriginal = EventTarget.prototype.addEventListener
    const removeOriginal = EventTarget.prototype.removeEventListener

    Object.defineProperty(this, 'addEventListener', {
      configurable: true,
      writable: true,
      value: function (
        this: OASButton,
        type: string,
        listener: EventListenerOrEventListenerObject | null,
        options?: boolean | AddEventListenerOptions,
      ) {
        if (type === 'oas-click' && typeof listener === 'function') {
          const fn = listener as EventListener
          // 同一监听器重复注册时复用已有包装，避免双包装双触发
          let wrapper = self.wrappedListeners.get(fn)
          if (!wrapper) {
            wrapper = (e: Event) => {
              // EventListener 类型声明返回 void，实际宿主可能返回 Promise，按 unknown 取值
              const ret = (fn as (evt: Event) => unknown)(e)
              if (ret && typeof (ret as PromiseLike<unknown>).then === 'function') {
                self.autoPromises.push(ret as PromiseLike<unknown>)
              }
            }
            self.wrappedListeners.set(fn, wrapper)
          }
          return addOriginal.call(this, type, wrapper, options)
        }
        return addOriginal.call(this, type, listener, options)
      },
    })

    Object.defineProperty(this, 'removeEventListener', {
      configurable: true,
      writable: true,
      value: function (
        this: OASButton,
        type: string,
        listener: EventListenerOrEventListenerObject | null,
        options?: boolean | EventListenerOptions,
      ) {
        if (type === 'oas-click' && typeof listener === 'function') {
          const fn = listener as EventListener
          const wrapped = self.wrappedListeners.get(fn)
          if (wrapped) self.wrappedListeners.delete(fn)
          return removeOriginal.call(this, type, wrapped ?? listener, options)
        }
        return removeOriginal.call(this, type, listener, options)
      },
    })
  }

  /** 当前是否处于加载态：布尔 loading 属性（空值/任意值均算，仅 "auto" 除外）或 auto 自动加载中 */
  private isLoading(): boolean {
    const raw = this.getAttribute('loading')
    return (raw != null && raw !== 'auto') || this.autoLoading
  }

  /** 退出 loading="auto" 自动加载态（宿主 Promise resolve/reject 后调用） */
  private exitAutoLoading(): void {
    if (!this.autoLoading) return
    this.autoLoading = false
    this.autoPromises = []
    this.update()
  }

  protected override render(): void {
    this.shadow.innerHTML = this.template()
    this.bind()
  }

  /** 真水合：校验 SSR 快照结构（关键节点 button/a[part=button] 存在）后直接接管，跳过 shadow 重建 */
  protected override hydrate(): boolean {
    if (!this.shadow.querySelector('button[part="button"], a[part="button"]')) return false
    this.bind()
    return true
  }

  /** href 增删会改变内部元素类型（button ↔ a），需重建 shadow；target 内联在模板中，增删同走重建；其余属性走 update() */
  override attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if ((name === 'href' || name === 'target') && this.hasRendered) {
      this.shadow.innerHTML = this.template()
      this.bind()
      this.update()
      return
    }
    super.attributeChangedCallback(name, oldValue, newValue)
  }

  protected override update(): void {
    if (!this.btn) return
    const type = this.getAttr('type', 'default') as ButtonType
    // size 就近读取 config-provider 注入值（自身属性 > config-provider > medium）
    const sizeRaw = this.injectValue('size', 'medium') as ButtonSize
    const { value: size, isValid: sizeValid } = normalizeSizeStrict(sizeRaw, ALL_SIZES, 'medium')
    if (!sizeValid) warnInvalidSize(sizeRaw)
    // disabled 就近读取全局禁用注入：组件显式 disabled > disabled-skip/disabledExempt 豁免 > provider 注入
    const disabled = this.injectDisabled()
    const disabledFocusable = this.hasAttr('disabled-focusable')
    const loading = this.isLoading()
    const loadingText = this.getAttr('loading-text', '')
    const icon = this.getAttr('icon', '')
    const iconEnd = this.getAttr('icon-end', '')
    const ghost = this.hasAttr('ghost')
    const block = this.hasAttr('block')
    const round = this.hasAttr('round')
    const circle = this.hasAttr('circle')
    const plain = this.hasAttr('plain')
    const iconPosition = this.getAttr('icon-position', 'start')
    const link = this.isLink()
    // variant 形态维度：显式 variant 属性优先；未显式设置时读 config-provider config JSON
    // 的 `oas-button.variant` 注入值；否则按旧属性映射（ghost→outlined、plain→filled、type=text→text），默认 solid
    const explicitVariant = this.getAttr('variant', '') as ButtonVariant | ''
    const injectedVariant = readConfigValue<unknown>(this, 'oas-button', 'variant')
    const variantCandidate =
      explicitVariant !== '' ? explicitVariant : typeof injectedVariant === 'string' ? injectedVariant : ''
    const variant: ButtonVariant = (VALID_BUTTON_VARIANTS as readonly string[]).includes(variantCandidate)
      ? (variantCandidate as ButtonVariant)
      : ghost
        ? 'outlined'
        : plain
          ? 'filled'
          : type === 'text'
            ? 'text'
            : 'solid'
    const color = this.getAttr('color', '')
    const wave = this.getAttr('wave', 'true') !== 'false'
    const active = this.hasAttr('active')

    // active 选中态 → host aria-pressed="true"（与 button-group 选中视觉同一 CSS 挂点，
    // 选中样式全部由既有 :host([aria-pressed]) 规则承载）。受控语义：组件不自切，
    // 宿主监听 oas-click 回写 active。所有权标记见字段注释
    if (active) {
      this.setAttribute('aria-pressed', 'true')
      this.ownedAriaPressed = true
    } else if (this.ownedAriaPressed) {
      this.ownedAriaPressed = false
      if (this.getAttribute('aria-pressed') === 'true') this.removeAttribute('aria-pressed')
    }

    // active-tint 选中着色（ui-spec §4.1 color 协议）：预设名 → --oas-preset-*-text 档
    // （预设基色对白字对比不达标，实底选中与 toggle-button 同取更深一档）；任意色值原样注入，
    // 文字安全档走主题感知混合（--oas-deep-mix/--oas-deep-sink，light 掺近黑压深 / dark 掺近白提亮）、
    // 实底文字按底色亮度取黑/白。仅在属性在场时注入，缺省零回归
    const tint = this.getAttr('active-tint', '').trim()
    if (tint) {
      const isPreset = (PRESET_TINTS as readonly string[]).includes(tint)
      const base = isPreset ? `var(--oas-preset-${tint}-text)` : tint
      this.btn.style.setProperty('--oas-button-active-tint', base)
      // 文字安全档：预设名/变量写法无法 JS 算亮度——预设直接用 -text 档，字面色才走混合派生
      this.btn.style.setProperty(
        '--oas-button-active-tint-text',
        isPreset ? base : `color-mix(in srgb, ${tint} var(--oas-deep-mix, 72%), var(--oas-deep-sink, black))`,
      )
      const onColor = isPreset ? '' : pickOnColor(tint)
      if (onColor) this.btn.style.setProperty('--oas-button-active-tint-on', onColor)
      else this.btn.style.removeProperty('--oas-button-active-tint-on')
    } else {
      this.btn.style.removeProperty('--oas-button-active-tint')
      this.btn.style.removeProperty('--oas-button-active-tint-text')
      this.btn.style.removeProperty('--oas-button-active-tint-on')
    }

    const hasLeadingIcon = icon !== '' && lookupIcon(icon) !== undefined
    const hasEndIcon = iconEnd !== '' && lookupIcon(iconEnd) !== undefined
    if (icon !== '' && !hasLeadingIcon) hintUnresolvedIcon(icon)
    if (iconEnd !== '' && !hasEndIcon) hintUnresolvedIcon(iconEnd)
    const hasIcon = hasLeadingIcon || hasEndIcon
    const hasText = (this.textContent ?? '').trim().length > 0
    const iconOnly = hasIcon && !hasText

    this.btn.className = [
      type,
      size,
      // variant 形态 class（solid 为默认不加 class；旧属性 ghost/plain 仍保留 class 兼容旧 CSS）
      variant !== 'solid' ? variant : '',
      ghost ? 'ghost' : '',
      block ? 'block' : '',
      round ? 'round' : '',
      circle ? 'circle' : '',
      plain ? 'plain' : '',
      iconPosition === 'end' ? 'icon-end' : '',
      hasIcon ? 'has-icon' : '',
      iconOnly ? 'icon-only' : '',
      wave ? 'wave' : '',
      this.hasAttr('wrap') ? 'wrap' : '',
      color ? 'has-color' : '',
      disabledFocusable ? 'disabled-focusable' : '',
      loading ? 'loading' : '',
      loading && loadingText ? 'loading-with-text' : '',
    ]
      .filter(Boolean)
      .join(' ')
    // color 自定义色：覆盖 type 语义色（经 --oas-button-color 变量，CSS 配色处兜底引用）
    if (color) {
      this.btn.style.setProperty('--oas-button-color', color)
      // 文字安全档（outlined/dashed/filled 文字用）：主题感知混合派生——
      // light fallback 72% 掺黑压深，dark 由主题 token（--oas-deep-mix/--oas-deep-sink）掺近白提亮
      this.btn.style.setProperty(
        '--oas-button-color-deep',
        `color-mix(in srgb, ${color} var(--oas-deep-mix, 72%), var(--oas-deep-sink, black))`,
      )
      // 实心底文字色按底色亮度取黑/白（on-primary 对中间调自定义色可能不可读）
      this.btn.style.setProperty('--oas-button-on-color', pickOnColor(color))
    } else {
      this.btn.style.removeProperty('--oas-button-color')
      this.btn.style.removeProperty('--oas-button-color-deep')
      this.btn.style.removeProperty('--oas-button-on-color')
    }
    this.btn.setAttribute('aria-busy', loading ? 'true' : 'false')
    // aria-disabled 语义：disabled/loading 真禁用；disabled-focusable 视觉禁用但可聚焦
    const ariaDisabled = disabled || loading || disabledFocusable
    // 链接按钮（a）无 disabled 属性，用 aria-disabled 承载禁用语义 + CSS 禁用态；button 用原生 disabled
    if (link) {
      this.btn.setAttribute('aria-disabled', ariaDisabled ? 'true' : 'false')
      // download/rel 透传（href 模式）：变化经 update 增量同步，不重建 DOM
      const download = this.getAttr('download', '')
      const rel = this.getAttr('rel', '')
      if (this.hasAttr('download')) this.btn.setAttribute('download', download)
      else this.btn.removeAttribute('download')
      if (rel) this.btn.setAttribute('rel', rel)
      else this.btn.removeAttribute('rel')
    } else {
      ;(this.btn as HTMLButtonElement).disabled = disabled || loading
      if (disabledFocusable) this.btn.setAttribute('aria-disabled', 'true')
      else this.btn.removeAttribute('aria-disabled')
    }

    const spinner = this.btn.querySelector<HTMLElement>('.spinner')
    if (spinner) {
      // loading-icon 插槽有实质内容时切自定义图标形态（内置 spinner 环停用，CSS 侧 .custom-icon）
      const slot = spinner.querySelector<HTMLSlotElement>('slot[name="loading-icon"]')
      const hasCustomIcon =
        !!slot &&
        slot.assignedNodes().some((n) => n.nodeType === Node.ELEMENT_NODE || (n.textContent ?? '').trim() !== '')
      spinner.classList.toggle('custom-icon', hasCustomIcon)
      spinner.hidden = !loading
    }

    // loading-text：loading 时替换 label 显示（hidden 由 update 控制显隐）
    const loadingTextEl = this.btn.querySelector<HTMLElement>('.loading-text')
    if (loadingTextEl) {
      loadingTextEl.textContent = loadingText
      loadingTextEl.hidden = !(loading && loadingText)
    }

    // 图标：lookupIcon 查表内联 SVG（跟随 currentColor，装饰性对读屏隐藏）
    const iconEl = this.btn.querySelector<HTMLElement>('.icon')
    if (iconEl) this.renderIcon(iconEl, icon)
    const endIconEl = this.btn.querySelector<HTMLElement>('[part="icon-end"]')
    if (endIconEl) this.renderIcon(endIconEl, iconEnd)

    // 可访问名称：宿主 aria-label 优先；纯图标无文字时以图标名兜底
    const hostLabel = this.getAttribute('aria-label')
    if (hostLabel) {
      this.btn.setAttribute('aria-label', hostLabel)
    } else if (iconOnly) {
      this.btn.setAttribute('aria-label', icon || iconEnd)
    } else {
      this.btn.removeAttribute('aria-label')
    }

    // 中文间自动空格（默认关，opt-in）：两个连续汉字之间插入空格（中文排版优化）
    this.applyAutoInsertSpace()
  }

  /** 向图标容器注入 lookupIcon 查表内联 SVG：空名/无效名时隐藏（跟随 currentColor，装饰性对读屏隐藏） */
  private renderIcon(el: HTMLElement, name: string): void {
    const content = name ? lookupIcon(name) : undefined
    el.hidden = !content
    el.innerHTML = ''
    if (content) {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
      svg.setAttribute('viewBox', '0 0 16 16')
      svg.setAttribute('width', '1em')
      svg.setAttribute('height', '1em')
      svg.setAttribute('aria-hidden', 'true')
      svg.setAttribute('focusable', 'false')
      svg.innerHTML = content
      el.appendChild(svg)
    }
  }

  /** 中文间自动空格：slot 文本里两个连续 CJK 字符间插入空格（auto-insert-space 默认关，opt-in） */
  private applyAutoInsertSpace(): void {
    if (!this.hasAttr('auto-insert-space') || this.getAttr('auto-insert-space', '') === 'false') return
    // 遍历宿主直接文本节点，两个连续汉字间插空格（不破坏元素子节点结构）
    for (const node of Array.from(this.childNodes)) {
      if (node.nodeType !== Node.TEXT_NODE) continue
      const text = node.textContent ?? ''
      const spaced = text.replace(/([\u4e00-\u9fa5])([\u4e00-\u9fa5])/g, '$1 $2')
      if (spaced !== text) node.textContent = spaced
    }
  }
}
